import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import dayjs from "dayjs";

import { dataGridStyle, palette } from "../../theme";
import API from "../../api";
import Header from "../../components/Header";
import { StandardGridToolbar } from "../../components/ToolbarKit";
import { MUTED } from "../../scenes-reseller/dashboard/dashboard-components/resellerUi";

const formatDateTime = (v) => {
  if (!v) return "-";
  const d = dayjs(v);
  return d.isValid() ? d.format("DD/MM/YYYY HH:mm") : "-";
};

const Muted = () => (
  <Typography component="span" sx={{ fontSize: 13, color: MUTED }}>
    -
  </Typography>
);

const textCell = (params) =>
  params.value !== undefined && params.value !== null && params.value !== "" ? params.value : <Muted />;

const columns = [
  {
    field: "timeStamp",
    headerName: "Timestamp",
    width: 160,
    // La data resta un valore reale: l'ordinamento e' cronologico, non testuale
    valueGetter: (value) => (value ? new Date(value) : null),
    renderCell: (params) => formatDateTime(params.value),
  },
  { field: "email", headerName: "Email", flex: 1, minWidth: 200, renderCell: textCell },
  { field: "statusCode", headerName: "Status code", width: 120, renderCell: textCell },
  { field: "ip", headerName: "IP", width: 150, renderCell: textCell },
  { field: "userAgent", headerName: "User agent", flex: 2, minWidth: 220, renderCell: textCell },
  { field: "type", headerName: "Type", flex: 1, minWidth: 160, renderCell: textCell },
  {
    field: "success",
    headerName: "Success",
    width: 110,
    renderCell: (params) => {
      const v = params.value;
      if (v === undefined || v === null || v === "") return <Muted />;
      const ok = v === true || String(v).toLowerCase() === "true";
      return (
        <Typography
          component="span"
          sx={{ fontSize: 13, color: ok ? undefined : palette.error.light }}
        >
          {String(v)}
        </Typography>
      );
    },
  },
];

export default function AuditByUsername() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quickFilter, setQuickFilter] = useState("");
  const [logs, setLogs] = useState([]);

  const fetchAudit = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await API.getAuditByUser();
      setLogs(res.data);
    } catch (e) {
      setError(
        e?.response?.data?.error ||
          e?.message ||
          "Error while loading audit logs"
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAudit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const data = Array.isArray(logs) ? logs : [];

    const mapped = data.map((doc, idx) => ({
      ...doc,
      id: doc._id ?? `EMAIL-${idx}`,
    }));

    setRows(mapped);
  }, [logs]);

  const filteredRows = useMemo(() => {
    const q = quickFilter.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [
        formatDateTime(r.timeStamp),
        r.email,
        r.statusCode,
        r.ip,
        r.userAgent,
        r.type,
        r.success,
      ].some((v) => String(v ?? "").toLowerCase().includes(q))
    );
  }, [rows, quickFilter]);

  const toolbar = (
    <TextField
      size="small"
      placeholder="Search email, IP, type, status…"
      value={quickFilter}
      onChange={(e) => setQuickFilter(e.target.value)}
      sx={{ minWidth: 240, width: "100%", maxWidth: 360 }}
      slotProps={{
        input: {
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: MUTED }} />
            </InputAdornment>
          ),
        },
      }}
    />
  );

  return (
    <Box m={"20px"}>
      <Header title={"Audit logs"} />

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box mt={2} mb={2} sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
        {toolbar}
        <Typography sx={{ fontSize: 13, color: MUTED }}>{filteredRows.length} logs</Typography>
      </Box>
      <Box sx={{ height: "calc(100vh - 290px)", minHeight: 460 }}>
        <DataGrid
          sx={dataGridStyle}
          rows={filteredRows}
          columns={columns}
          loading={loading}
          slots={{ toolbar: StandardGridToolbar }}
          showToolbar
          pageSizeOptions={[10, 25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
            sorting: { sortModel: [{ field: "timeStamp", sort: "desc" }] },
          }}
          disableRowSelectionOnClick
          localeText={{ noRowsLabel: "No logs found" }}
        />
      </Box>
    </Box>
  );
}
